import React, { useState, useEffect } from 'react';
import { semarData } from '../data/semarData';
import { database } from '../firebase';
import {
    ref,
    get,
    set,
    update,
    onValue,
    serverTimestamp,
    query as dbQuery,
    orderByChild,
    limitToLast
} from 'firebase/database';
import { signInAnonymously } from 'firebase/auth';
import { auth } from '../firebase';
import '../styles/AnniversaryPage.css';

const AnniversaryPage = () => {
    const [email, setEmail] = useState('');
    const [referralCode, setReferralCode] = useState(null);
    const [referralCount, setReferralCount] = useState(0);
    const [ranking, setRanking] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

    // Estados de la Ruleta
    const [isSpinning, setIsSpinning] = useState(false);
    const [rotation, setRotation] = useState(0);
    const [prizeWon, setPrizeWon] = useState(null);
    const [hasSpun, setHasSpun] = useState(false);

    // Premios oficiales con stock: 20(1m), 15(2m), 10(6m), 5(1y)
    const prizes = [
        "1 MES PREMIUM", "SIGUE PARTICIPANDO", "BIMESTRE PREMIUM", "INTÉNTALO DE NUEVO",
        "SEMESTRAL PREMIUM", "SIGUE PARTICIPANDO", "ANUAL PREMIUM", "INTÉNTALO DE NUEVO"
    ];

    const [prizesStock, setPrizesStock] = useState({
        "1 MES PREMIUM": 0,
        "BIMESTRE PREMIUM": 0,
        "SEMESTRAL PREMIUM": 0,
        "ANUAL PREMIUM": 0
    });

    // Fecha exacta: 15 de Mayo 2026 a las 17:00 horas
    const anniversaryDate = new Date('2026-05-15T17:00:00').getTime();

    useEffect(() => {
        // Autenticación anónima para permitir lectura de nodos protegidos (config, licenses)
        signInAnonymously(auth).catch((error) => {
            console.error("Error en autenticación anónima:", error);
        });

        // Escuchar el stock de premios en tiempo real
        const stockRef = ref(database, 'anniversary_config/prizes_stock');
        const unsubscribeStock = onValue(stockRef, (snapshot) => {
            if (snapshot.exists()) {
                setPrizesStock(snapshot.val());
            }
        });
        const timer = setInterval(() => {
            const now = new Date().getTime();
            const distance = anniversaryDate - now;

            if (distance < 0) {
                clearInterval(timer);
                setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
                return;
            }

            setCountdown({
                days: Math.floor(distance / (1000 * 60 * 60 * 24)),
                hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
                minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
                seconds: Math.floor((distance % (1000 * 60)) / 1000),
            });
        }, 1000);

        // Leaderboard listener - Aumentado a 40 para la regla de negocio final
        const rankingRef = dbQuery(
            ref(database, 'anniversary_participants'),
            orderByChild('referralCount'),
            limitToLast(40)
        );

        const unsubscribe = onValue(rankingRef, (snapshot) => {
            const data = [];
            snapshot.forEach((child) => {
                data.push({
                    id: child.key,
                    ...child.val()
                });
            });
            // Firebase limits are "last", so we reverse it for Top 1 to be first
            setRanking(data.reverse());
        }, (error) => {
            console.error("Error al cargar el ranking:", error);
            setErrorMessage("Error de conexión con la base de datos de posiciones.");
        });

        return () => {
            clearInterval(timer);
            unsubscribeStock();
            unsubscribe();
        };
    }, [anniversaryDate]);

    // Firebase keys don't allow . or @
    const encodeEmail = (emailToEncode) => {
        return emailToEncode.toLowerCase().trim().replace(/@/g, '_at_').replace(/\./g, '_dot_');
    };

    // Advanced anonymization: fhernando6767@gmail.com -> f****a******7@gmail.com
    const maskEmail = (emailToMask) => {
        if (!emailToMask) return "";
        const [user, domain] = emailToMask.split('@');
        if (user.length < 5) return user[0] + "*".repeat(user.length - 1) + "@" + domain;

        const first = user[0];
        const last = user[user.length - 1];
        const middleIndex = Math.floor(user.length / 2) - 1;
        const middleChar = user[middleIndex];

        const stars1 = "*".repeat(middleIndex - 0);
        const stars2 = "*".repeat(user.length - middleIndex - 2);

        return `${first}${stars1}${middleChar}${stars2}${last}@${domain}`;
    };

    // Check if user has an active license in the App
    const checkAppLicense = async (encodedEmail) => {
        try {
            const licenseRef = ref(database, `licenses/${encodedEmail}`);
            const snapshot = await get(licenseRef);
            return snapshot.exists();
        } catch (error) {
            console.error("Error checking license:", error);
            return false;
        }
    };

    // Check if user is already registered for the anniversary
    const checkAnniversaryRegistration = async (encodedEmail) => {
        try {
            const participantRef = ref(database, `anniversary_participants/${encodedEmail}`);
            const snapshot = await get(participantRef);
            if (snapshot.exists()) {
                const userData = snapshot.val();
                setReferralCode(userData.referralCode);
                setReferralCount(userData.referralCount || 0);
                setHasSpun(userData.hasSpun || false);
                setPrizeWon(userData.prizeWon || null);
                return true;
            }
            return false;
        } catch (error) {
            console.error("Error checking registration:", error);
            return false;
        }
    };

    const handleSpin = async () => {
        if (isSpinning || hasSpun || !referralCode) return;

        // Validar si el evento ya comenzó (15 de Mayo)
        const now = new Date().getTime();
        if (now < anniversaryDate) {
            setErrorMessage("La ruleta se activará el 15 de Mayo al finalizar la cuenta regresiva.");
            return;
        }

        // Validar si el usuario está en el Top 40 antes de permitir el giro
        const isEligible = ranking.some(p => p.referralCode === referralCode);
        if (!isEligible && ranking.length >= 40) {
            setErrorMessage("Lo sentimos, solo el Top 40 de participantes con más referidos podrán girar la ruleta.");
            return;
        }

        setIsSpinning(true);
        const segments = prizes.length;
        const degreesPerSegment = 360 / segments;

        // Pesos Normales para el Evento
        let weights = [
            prizesStock["1 MES PREMIUM"] > 0 ? 15 : 0,
            30, // Sigue participando
            prizesStock["BIMESTRE PREMIUM"] > 0 ? 10 : 0,
            30, // Inténtalo de nuevo
            prizesStock["SEMESTRAL PREMIUM"] > 0 ? 5 : 0,
            30, // Sigue participando
            prizesStock["ANUAL PREMIUM"] > 0 ? 2 : 0,
            30  // Inténtalo de nuevo
        ];

        // Bono por referidos para premios reales
        const bonus = Math.min(referralCount * 3, 40);
        if (weights[0] > 0) weights[0] += bonus;
        if (weights[2] > 0) weights[2] += bonus / 2;
        if (weights[4] > 0) weights[4] += bonus / 4;
        if (weights[6] > 0) weights[6] += bonus / 8;

        const totalWeight = weights.reduce((acc, w) => acc + w, 0);
        if (totalWeight === 0) {
            alert("No quedan premios disponibles en este momento.");
            setIsSpinning(false);
            return;
        }

        let randomWeight = Math.random() * totalWeight;
        let randomIndex = 0;
        for (let i = 0; i < weights.length; i++) {
            randomWeight -= weights[i];
            if (randomWeight <= 0) { randomIndex = i; break; }
        }

        const extraDegrees = 3600;
        const segmentAngle = randomIndex * degreesPerSegment + (degreesPerSegment / 2);
        const finalRotation = rotation + extraDegrees - segmentAngle - (rotation % 360);

        setRotation(finalRotation);

        setTimeout(async () => {
            const result = prizes[randomIndex];
            setIsSpinning(false);

            // Si el premio es "Sigue participando" o "Inténtalo de nuevo", permitimos re-intento
            const isRetry = result === "SIGUE PARTICIPANDO" || result === "INTÉNTALO DE NUEVO";

            if (!isRetry) {
                // Es un premio real, descontamos stock y marcamos como ya girado
                const encodedEmail = encodeEmail(email || localStorage.getItem('userEmail'));
                try {
                    // 1. Marcar usuario como ganador y guardar premio
                    await update(ref(database, `anniversary_participants/${encodedEmail}`), {
                        hasSpun: true,
                        prizeWon: result,
                        spinDate: serverTimestamp()
                    });

                    // 2. Descontar stock atómicamente
                    const updates = {};
                    updates[`anniversary_config/prizes_stock/${result}`] = (prizesStock[result] || 1) - 1;
                    await update(ref(database), updates);

                    setPrizeWon(result);
                    setHasSpun(true);
                } catch (error) {
                    console.error("Error al persistir premio:", error);
                }
            } else {
                alert(`¡${result}! Puedes girar de nuevo.`);
            }
        }, 5000);
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrorMessage('');

        const cleanEmail = email.toLowerCase().trim();
        const encodedEmail = encodeEmail(cleanEmail);

        try {
            // 1. MUST have a license in the App
            const hasLicense = await checkAppLicense(encodedEmail);
            if (!hasLicense) {
                setErrorMessage("ERROR: Debes estar registrado en la App Ascensos App Vortex para participar.");
                setLoading(false);
                return;
            }

            // 2. Check if already registered for anniversary
            const exists = await checkAnniversaryRegistration(encodedEmail);
            if (exists) {
                setLoading(false);
                return;
            }

            // 3. Generate and save
            const newCode = `VORTEX-${Math.random().toString(36).substr(2, 4).toUpperCase()}`;

            const participantData = {
                email: cleanEmail,
                referralCode: newCode,
                referralCount: 0,
                registrationDate: serverTimestamp()
            };

            const updates = {};
            updates[`anniversary_participants/${encodedEmail}`] = participantData;
            updates[`referral_codes/${newCode}`] = encodedEmail; // Mapa inverso para Android

            await update(ref(database), updates);

            setReferralCode(newCode);
            setReferralCount(0);
        } catch (error) {
            console.error("Error en registro:", error);
            setErrorMessage("Hubo un error técnico. Por favor intenta más tarde.");
        } finally {
            setLoading(false);
        }
    };

    const shareUrl = `https://play.google.com/store/apps/details?id=com.vortex.ascensosappvortex&referrer=${referralCode}`;
    const whatsappText = `¡Únete al 1er Aniversario de Ascensos App Vortex! Regístrate aquí y participa en el sorteo de suscripciones premium: ${shareUrl}`;

    return (
        <div className="anniversary-container">
            <div className="tech-background">
                <div className="grid-overlay"></div>
                <div className="hexagon-mask"></div>
            </div>

            <header className="anniversary-header">
                <h1 className="glitch-text">1ER ANIVERSARIO</h1>
                <h2>Ascensos App Vortex</h2>
                <p className="motto">"Honor, Deber, Estudio"</p>
            </header>

            {errorMessage && (
                <div className="error-banner">
                    {errorMessage}
                    <button onClick={() => setErrorMessage('')} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', float: 'right' }}>&times;</button>
                </div>
            )}

            <section className="privacy-banner">
                <div className="privacy-content">
                    <span className="warning-icon">🛡️</span>
                    <div>
                        <p><strong>PRIVACIDAD TOTAL GARANTIZADA:</strong> En Ascensos App Vortex estamos comprometidos con la privacidad del usuario, es por eso que no pedimos información personal.</p>
                        <p style={{ marginTop: '5px', fontSize: '0.9rem', color: 'var(--gold-matte)' }}>Tu correo electrónico se guarda de forma codificada y solo se utiliza para validar tu participación y asignar tus premios de forma segura.</p>
                    </div>
                </div>
            </section>

            <section className="countdown-section">
                <div className="timer-grid">
                    <div className="timer-item"><span>{countdown.days}</span><label>Días</label></div>
                    <div className="timer-item"><span>{countdown.hours}</span><label>Horas</label></div>
                    <div className="timer-item"><span>{countdown.minutes}</span><label>Minutos</label></div>
                    <div className="timer-item"><span>{countdown.seconds}</span><label>Segundos</label></div>
                </div>
            </section>

            {!referralCode ? (
                <section className="registration-card">
                    <h3>Registro</h3>
                    <form onSubmit={handleRegister}>
                        <div className="input-group">
                            <label>Correo Electrónico (vinculado a la App)</label>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="usuario@correo.com"
                                style={{ fontSize: '1.2rem', padding: '1.2rem' }}
                            />
                            <p style={{ fontSize: '0.75rem', marginTop: '10px', opacity: 0.7 }}>
                                * Este correo debe ser el mismo que usas en la App Android para recibir tu premio automáticamente.
                            </p>
                        </div>

                        <button type="submit" className="btn-tactical" disabled={loading}>
                            {loading ? "PROCESANDO..." : "GENERAR MI CÓDIGO"}
                        </button>
                    </form>
                </section>
            ) : (
                <section className="success-card">
                    <h3>¡Misión Cumplida!</h3>
                    <p>Tu código de referido es:</p>
                    <div className="referral-box">{referralCode}</div>

                    <div className="stats-box">
                        <div className="stat-item">
                            <span className="stat-value">{referralCount}</span>
                            <span className="stat-label">Compañeros Invitados</span>
                        </div>
                    </div>

                    <div className="share-buttons">
                        <button className="btn-tactical" onClick={() => navigator.clipboard.writeText(shareUrl)}>
                            COPIAR LINK
                        </button>
                        <a
                            href={`https://wa.me/?text=${encodeURIComponent(whatsappText)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-whatsapp"
                        >
                            COMPARTIR EN WHATSAPP
                        </a>
                    </div>
                </section>
            )}

            <section className="bases-sorteo">
                <div className="bases-card">
                    <h3>Bases del Sorteo</h3>
                    <ul>
                        <li><strong>Registro:</strong> Ingresa el correo vinculado a tu cuenta de Ascensos App Vortex.</li>
                        <li><strong>Invitaciones:</strong> Comparte tu código único con otros compañeros que aún no tengan la App instalada.</li>
                        <li><strong>Probabilidades:</strong> Cada descarga verificada con tu código suma puntos para el sorteo final.</li>
                        <li><strong>Privacidad:</strong> No solicitamos datos personales, adscripciones ni nombres para proteger tu identidad.</li>
                        <li><strong>Seguridad:</strong> Tu correo se procesa mediante algoritmos de encriptación de una sola vía.</li>
                    </ul>
                </div>
            </section>

            <section className="roulette-preview">
                <h3>Ruleta de Premios de Aniversario</h3>
                <div className="roulette-outer">
                    <div className="roulette-wrapper">
                        <div className="roulette-pointer"></div>
                        <div
                            className={`roulette-wheel ${isSpinning ? 'spinning' : ''}`}
                            style={{ transform: `rotate(${rotation}deg)` }}
                        >
                            {prizes.map((prize, index) => (
                                <React.Fragment key={index}>
                                    <div
                                        className="roulette-segment"
                                        style={{ '--index': index, '--total': prizes.length }}
                                    ></div>
                                    <div
                                        className="roulette-label"
                                        style={{ '--index': index, '--total': prizes.length }}
                                    >
                                        <span className="prize-text">{prize}</span>
                                    </div>
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="roulette-controls">
                    {!referralCode ? (
                        <p className="roulette-hint">Regístrate arriba para activar tu giro de aniversario.</p>
                    ) : (
                        <>
                            {!hasSpun ? (
                                <button
                                    className={`btn-tactical btn-spin ${new Date().getTime() < anniversaryDate ? 'locked' : ''}`}
                                    onClick={handleSpin}
                                    disabled={isSpinning || new Date().getTime() < anniversaryDate}
                                >
                                    {isSpinning ? "GIRANDO..." :
                                        new Date().getTime() < anniversaryDate ? "BLOQUEADO HASTA EL 15 DE MAYO" : "¡PROBAR MI SUERTE!"}
                                </button>
                            ) : (
                                <div className="prize-announcement">
                                    <h4>¡Felicidades!</h4>
                                    <p>Has ganado: <strong>{prizeWon}</strong></p>
                                    <p className="prize-note">Ponte en contacto con soporte si ganaste una suscripción.</p>
                                    <button
                                        className="btn-tactical outline"
                                        onClick={() => { setPrizeWon(null); setRotation(0); }}
                                    >
                                        VER RULETA DE NUEVO
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </section>

            <section className="leaderboard-section">
                <h3>Tabla de Posiciones (Top 40)</h3>
                <div className="leaderboard-card">
                    <table className="leaderboard-table">
                        <thead>
                            <tr>
                                <th>Pos</th>
                                <th>Usuario (Anonimizado)</th>
                                <th>Referidos</th>
                                <th>Premio obtenido</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ranking.length > 0 ? ranking.map((user, index) => (
                                <tr key={user.id} className={user.referralCode === referralCode ? 'highlight' : ''}>
                                    <td>{index + 1}</td>
                                    <td className="masked-email">{maskEmail(user.email)}</td>
                                    <td className="count">{user.referralCount}</td>
                                    <td style={{
                                        fontWeight: 'bold',
                                        color: user.prizeWon ? 'var(--gold-matte)' : '#888',
                                        fontSize: '0.8rem'
                                    }}>
                                        {user.prizeWon || 'Pendiente girar'}
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="4">Iniciando transmisiones de datos...</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>

            <section className="bases-card">
                <h3>Pasos para participar en el Concurso de Aniversario</h3>
                <ul>
                    <li>
                        <span className="step-num">1</span>
                        <p>Únicamente ingresa tu correo electrónico vinculado a la App. **Garantizamos el anonimato total de tu identidad.**</p>
                    </li>
                    <li>
                        <span className="step-num">2</span>
                        <p>Comparte tu código único con otros compañeros que aún no tengan la App instalada, cada descarga exitosa desde la App Android cuenta como un referido.</p>
                    </li>
                    <li>
                        <span className="step-num">3</span>
                        <p>Al finalizar la cuenta regresiva, solo el <strong>Top 40 de participantes</strong> con más referidos podrá girar la ruleta.</p>
                    </li>
                    <li>
                        <span className="step-num">4</span>
                        <p>¡Gira la ruleta y obtén tu premio garantizado! Los 40 primeros participantes con más referidos tendran un regalo seguro.</p>
                    </li>
                </ul>
            </section>

            <footer className="anniversary-footer">
                <p>&copy; 2026 Vortex Ascensos App - Desarrollado para la Excelencia Naval</p>
                <p className="support-link">¿Necesitas soporte? <a href="mailto:ascensosappvortex@gmail.com">Contáctanos</a></p>
            </footer>
        </div>
    );
};

export default AnniversaryPage;
